import { memo } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { ascend, assoc, map, path, pipe, prop, sortWith } from 'ramda'
import {
  Box,
  Button,
  IconArrowUpRight,
  IconLockOutline,
  useDialogController
} from '@machobear/design-system'
import { useSelector } from 'react-redux'

import {
  BuildingFeature,
  BuildingRestriction,
  ListingVerifiedtateMachine,
  PriceFrequency
} from '@/types'
import { EnhancedBuilding } from '@/models/building'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { RootState } from '@/lib/redux'
import {
  formatPrice,
  getPaymentFrequency,
  getUrl,
  isGroup
} from '@/utils/listings'
import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionComplete from '@/hooks/use-session-complete'
import BlurredContentLoader from '@/components/blurred-content-loader'
import CdnImage from '@/components/cdn-image'
import { useListImpressionTracking } from '@/components/ImpressionProvider/useListImpressionTracking'
import ListingSection from '@/components/listing-section'
import Skeleton from '@/components/skeleton'
import { useTermsDialogContext } from '@/components/terms-dialog/context'
import VerifiedBadge from '@/components/verified-badge'

import DSLoginModal from '../Auth/login-modal/ds-login-modal'
import Icon from './icon'
import * as styles from './index.css'
import { Sidebar, TabContainer, TabContent } from './styles'

const List = styled.ul`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin: 0 0 40px;
  padding: 0;
  width: 100%;
`

const Item = styled.li`
  display: flex;
  align-items: center;
`

const Empty = styled.p`
  margin: 15px 0 0 0;
`

const Text = styled.p`
  margin: 0 0 0 10px;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: 400;
  line-height: 16px;
`

const Description = styled.p`
  color: ${colors.regular};
  font-size: ${fontSizes.regularMobile};
  line-height: 20px;
  white-space: pre-line;
  margin: 0 0 10px;
`

const SidebarCustom = styled(Sidebar)`
  background: ${colors.white};
  margin-bottom: 0;

  @media screen and (min-width: ${breakpoints.desktop}) {
    align-self: flex-start;
  }
`

const SidebarContent = styled.div`
  min-height: 60px;
  line-height: 26px;
  box-shadow: none;
  box-shadow: inset 0 -1px 0 0 rgba(151, 151, 151, 0.5);
  display: flex;
  justify-content: space-between;
  width: 100%;
  text-align: right;

  &:last-child {
    box-shadow: none;
  }
`

const SidebarTitle = styled.p`
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.regular};
  margin: 16px 20px 16px 0;
`

const SidebarText = styled(SidebarTitle)`
  font-weight: ${fontWeights.medium};
`

const CustomIcon = styled(Icon)`
  flex-shrink: 0;
`

const BuildingListingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 30px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: 100%;
  }
`

const ListingContainer = styled.div<{ isListingCardLocked?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid ${colors.grey};
  position: relative;

  ${props =>
    props.isListingCardLocked &&
    `
   &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(3px);
  }
  `}

  :last-child {
    border-bottom: none;
  }
`

const BedBathContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
  width: 134px;
`

const ListingImageWrapper = styled.div`
  width: 90px;
  height: 60px;
  margin-right: 20px;
  position: relative;
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-right: 10px;
  }
`

const PriceContainer = styled.div`
  width: 130px;
`

const ViewDetailsText = styled.a`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.link};
  text-decoration: underline;
  margin: 0;
  white-space: nowrap;
`

const ListingInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-right: auto;
  width: 50%;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`

const renderItem = (item: Partial<BuildingFeature>) => (
  <Item key={item.id}>
    <CustomIcon id={item.id} width={34} />
    <Text>{item.name}</Text>
  </Item>
)

interface ListingWrapperProps {
  list: BuildingFeature[]
  getItem: <T extends BuildingFeature>(
    a: T
  ) => {
    id: string
    name: string | undefined
  }
}

const ListWrapper: React.FC<ListingWrapperProps> = ({ list = [], getItem }) => {
  const { t } = useTranslation()
  if (!list.length)
    return (
      <Empty>
        {t('building:b.building.view.no_info.text', 'No information to show')}
      </Empty>
    )

  return <List>{map(pipe(getItem, renderItem), list)}</List>
}

const ListingSkeleton = () => (
  <>
    <ListingContainer>
      <ListingImageWrapper>
        <Skeleton width={90} height={60} />
      </ListingImageWrapper>
      <ListingInfoContainer>
        <Skeleton width={112} height={19} />
        <Skeleton width={98} height={19} />
      </ListingInfoContainer>
      <Skeleton width={88} height={19} />
    </ListingContainer>
  </>
)

interface Props {
  building: EnhancedBuilding
  buildingListings: BuildingDetail[]
}

type UnitTypeScopeGraphQL = 'ENTIRE' | 'SINGLE'
type BuildingTypeGraphQL = 'CONDO' | 'HOUSE'
interface BuildingDetail {
  listing_id: string
  price: string
  price_frequency: PriceFrequency
  gr_min_price: string
  gr_unit: string
  bedrooms: number
  bathrooms: number
  verified_state_machine: ListingVerifiedtateMachine
  cover_photo_aws_s3_key: string
  unit_type_scope_txt_id: UnitTypeScopeGraphQL
  building_type: BuildingTypeGraphQL
  city: string
  public_viewing: boolean
}

const Info: React.FC<Props> = memo(({ building, buildingListings }) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: 450px)`)
  const isLoggedIn = useSessionComplete()
  const { visible, showModal, closeModal } = useDialogController()
  const ensureLoggedIn = useEnsureLoggedIn({
    showLogin: showModal
  })

  useListImpressionTracking({
    id: 'building',
    minVisibleDuration: 0
  })

  const developers = useSelector<
    RootState,
    RootState['basic']['building_developers'] | undefined
  >(path(['basic', 'building_developers']))

  const restrictions = useSelector<
    RootState,
    RootState['basic']['building_restrictions'] | undefined
  >(path(['basic', 'building_restrictions']))

  const { isLoading, showModalForRequiredTerms, checkAgreementMismatch } =
    useTermsDialogContext()

  const isAgreementMismatch = checkAgreementMismatch('trreb_agreements')
  const isLoadingAgreements = isLoading && isLoggedIn
  const buildingDetail = buildingListings || []

  const renderListingsList = () => {
    const renderBedBath = (
      bedrooms: number,
      bathrooms: number,
      unit_type_scope_txt_id: UnitTypeScopeGraphQL
    ) => {
      if (unit_type_scope_txt_id === 'SINGLE') {
        return t('common.room', 'Room')
      }
      return `${
        bedrooms > 0
          ? `${bedrooms} ${t('common.bed', 'Bed')} `
          : `${t('common.studio', 'Studio')} `
      } · ${bathrooms} ${t('common.bath', 'Bath')}`
    }

    const formatedPrice = buildingDetail.map(listing =>
      assoc('price', listing.gr_min_price || listing.price)(listing)
    )
    const sortedListings = pipe<any, BuildingDetail[]>(
      sortWith([ascend(prop('bedrooms')), ascend(prop('price'))])
    )(formatedPrice)

    const listingsList = sortedListings.map(listingInfo => {
      const {
        listing_id,
        price,
        price_frequency,
        gr_unit,
        bedrooms,
        bathrooms,
        verified_state_machine,
        cover_photo_aws_s3_key,
        unit_type_scope_txt_id,
        building_type,
        city,
        public_viewing
      } = listingInfo
      const formattedPrice = isGroup({ gr_unit })
        ? `${formatPrice(price)}+`
        : formatPrice(price)

      const isMLSListing = public_viewing === false
      const isListingLocked =
        isMLSListing &&
        (isAgreementMismatch || !isLoggedIn || isLoadingAgreements)

      const listingLink = getUrl(listingInfo)
      const priceText = `${formattedPrice} / ${getPaymentFrequency(
        price_frequency
      )}`
      const bedBathText = renderBedBath(
        bedrooms,
        bathrooms,
        unit_type_scope_txt_id
      )

      return (
        <ListingContainer
          key={listing_id}
          isListingCardLocked={isListingLocked}
          data-impression
          data-impression-listingid={listing_id}
          data-impression-page="building-page"
          data-impression-section="listing"
          data-impression-sponsored="false"
        >
          <ListingImageWrapper>
            <CdnImage
              src={cover_photo_aws_s3_key}
              alt={`${bedBathText} for rent ${priceText}`}
              sizes="90px"
              layout="fill"
              objectFit="cover"
              placeholder="blur"
            />
          </ListingImageWrapper>
          <ListingInfoContainer>
            <PriceContainer>
              <BlurredContentLoader blurred={isListingLocked}>
                <strong>{priceText}</strong>
              </BlurredContentLoader>
            </PriceContainer>
            <BedBathContainer>
              <BlurredContentLoader blurred={isListingLocked}>
                {bedBathText}{' '}
                {verified_state_machine === 'approved' && (
                  <VerifiedBadge verified type="listing" />
                )}
              </BlurredContentLoader>
            </BedBathContainer>
          </ListingInfoContainer>
          <BlurredContentLoader blurred={isListingLocked}>
            <Link
              href={getUrl({
                building_type,
                city,
                listing_id
              })}
              passHref
              legacyBehavior
            >
              <ViewDetailsText target="_blank" rel="noopener">
                {displayMobile
                  ? t('building:b.building.view.view.text', 'View')
                  : t(
                      'building:b.building.view.view_details.text',
                      'View details'
                    )}
              </ViewDetailsText>
            </Link>
          </BlurredContentLoader>

          <Box position="absolute" className={styles.buttonWrapperStyle}>
            {isMLSListing && !isLoggedIn ? (
              <Button
                icon={<IconLockOutline />}
                variant="ghost"
                onClick={() =>
                  ensureLoggedIn(() =>
                    showModalForRequiredTerms('trreb_agreements', () =>
                      window.open(listingLink, '_blank')
                    )
                  )
                }
              >
                {t('b.auth.view.login_to_view', 'Log in to View')}
              </Button>
            ) : isMLSListing && isAgreementMismatch ? (
              <Button
                icon={<IconArrowUpRight />}
                variant="ghost"
                id="listing_taptoview_click"
                onClick={() =>
                  showModalForRequiredTerms('trreb_agreements', () =>
                    window.open(listingLink, '_blank')
                  )
                }
              >
                {t('b.auth.view.tap_to_view', 'Tap to view')}
              </Button>
            ) : null}
          </Box>
        </ListingContainer>
      )
    })

    return (
      <ListingSection
        border={false}
        title={t(
          'building:b.building.view.listings_for_rent.text',
          'Listings for Rent'
        )}
      >
        <BuildingListingsContainer data-impression-container="true">
          {isLoadingAgreements ? (
            <>
              <ListingSkeleton />
              <ListingSkeleton />
              <ListingSkeleton />
            </>
          ) : (
            <>{listingsList}</>
          )}
        </BuildingListingsContainer>
      </ListingSection>
    )
  }

  return (
    <>
      <TabContainer reverse>
        <TabContent>
          {building.amenities && building.amenities.length > 0 && (
            <ListingSection
              title={t(
                'building:b.building.view.building_amenities.text',
                'Building Amenities'
              )}
            >
              <ListWrapper
                list={building.amenities}
                getItem={item => ({
                  id: item.txt_id,
                  name: t(`basic:b.building.amenities.${item.txt_id}.text`)
                })}
              />
            </ListingSection>
          )}
          {renderListingsList()}
          {building.restrictions && building.restrictions.length > 0 && (
            <ListingSection
              title={t(
                'building:b.building.view.building_restrictions.text',
                'Building Restrictions'
              )}
            >
              <ListWrapper
                list={building.restrictions}
                getItem={(item: BuildingFeature) => ({
                  id: (item as BuildingRestriction).building_restriction_txt_id,
                  name: restrictions?.[
                    (item as BuildingRestriction).building_restriction_txt_id
                  ].name
                })}
              />
            </ListingSection>
          )}
          {building.description && (
            <ListingSection
              title={t(
                'building:b.building.view.description.text',
                'Description'
              )}
            >
              <Description>{building.description}</Description>
            </ListingSection>
          )}
        </TabContent>
        <SidebarCustom>
          <ListingSection
            title={t('building:b.building.view.history.text', 'History')}
          >
            {building.building_developer_id &&
              developers?.[building.building_developer_id] && (
                <SidebarContent>
                  <SidebarTitle>
                    {t('building:b.building.view.developer.text', 'Developer')}
                  </SidebarTitle>
                  <SidebarText>
                    {developers[building.building_developer_id].name}
                  </SidebarText>
                </SidebarContent>
              )}
            <SidebarContent>
              <SidebarTitle>
                {t(
                  'building:b.building.view.completion_year.text',
                  'Completion Year'
                )}
              </SidebarTitle>
              <SidebarText>{building.year_built}</SidebarText>
            </SidebarContent>
            {building.neighborhood && (
              <SidebarContent>
                <SidebarTitle>
                  {t('common.neighbourhood', 'Neighbourhood')}
                </SidebarTitle>
                <SidebarText>{building.neighborhood}</SidebarText>
              </SidebarContent>
            )}
            {building.building_type_txt_id && (
              <SidebarContent>
                <SidebarTitle>
                  {t('building:b.building.view.type.text', 'Type')}
                </SidebarTitle>
                <SidebarText>
                  {t(
                    `building:b.building.view.building_type_${building.building_type_txt_id}.text`,
                    `${building.building_type_txt_id}`
                  )}
                </SidebarText>
              </SidebarContent>
            )}
          </ListingSection>
        </SidebarCustom>
      </TabContainer>
      <DSLoginModal
        t={t}
        mode="login"
        visible={visible}
        eventId="web-signup-submit-login-redirect-mls"
        onClose={closeModal}
      />
    </>
  )
})

Info.displayName = 'Memo'

export default Info
